import { ReadingType } from 'infrastructure/enums';

import { resolveOutcome } from './resolve-outcome';

import type { Answers, EngineState, ReadingContext, StepId } from './types';

/** All sleeping-mat variants (Resting Pulse) — none support a same-call recheck. */
export const SLEEPING_MAT_TYPES = new Set<ReadingType>([
  ReadingType.SleepingMat,
  ReadingType.SleepingMatDuration,
  ReadingType.SleepingMatActivity,
]);

const isUnableToReach = (a: Answers): boolean => a.contact === 'not-reached';

const computeVisibleSteps = (a: Answers, context: ReadingContext): StepId[] => {
  if (a.readingStatus === 'already-recovered') {
    return ['reading-status', 'outcome'];
  }
  const steps: StepId[] = ['reading-status', 'contact'];
  if (isUnableToReach(a)) {
    return [...steps, 'outcome'];
  }
  if (a.contact === 'reached') {
    steps.push('consent');
    if (a.consent === 'unable-to-verify') {
      return [...steps, 'outcome'];
    }
    if (a.consent === 'patient' || a.consent === 'representative') {
      // Sleep Mat (Resting Pulse) has no same-call recheck — skip #4. The reading can
      // arrive as any of the three sleeping-mat variants.
      if (!SLEEPING_MAT_TYPES.has(context.readingType)) {
        steps.push('recheck');
      }
      steps.push('symptoms', 'outcome');
    }
  }
  return steps;
};

export const computeEngineState = (
  a: Answers,
  context: ReadingContext,
): EngineState => {
  const visibleSteps = computeVisibleSteps(a, context);
  const noLiveCall =
    a.readingStatus === 'already-recovered' || isUnableToReach(a);
  const noRecheckStep = SLEEPING_MAT_TYPES.has(context.readingType);
  return {
    visibleSteps,
    currentStep: visibleSteps[visibleSteps.length - 1],
    preSelectedOutcome: resolveOutcome(a, { noRecheckStep }),
    noLiveCall,
  };
};
